import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save, MessageSquare } from "lucide-react";

export default function AdminWhatsApp() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/settings").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const set = (path, value) => {
    const clone = JSON.parse(JSON.stringify(data));
    const parts = path.split(".");
    let cur = clone;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setData(clone);
  };
  const save = async () => { try { await api.put("/settings", { data }); toast.success("Saved"); refresh(); } catch { toast.error("Save failed"); } };

  const F = ({ label, path, type = "text", rows }) => {
    const parts = path.split(".");
    let val = data; for (const p of parts) val = val?.[p] ?? "";
    const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
    if (type === "textarea") return (
      <div><label className="text-xs font-semibold text-neutral-600">{label}</label>
        <textarea rows={rows || 3} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`whatsapp-${path}`} /></div>
    );
    if (type === "checkbox") return (
      <label className="flex items-center gap-2"><input type="checkbox" checked={!!val} onChange={(e) => set(path, e.target.checked)} data-testid={`whatsapp-${path}`} /><span className="text-sm">{label}</span></label>
    );
    return (
      <div><label className="text-xs font-semibold text-neutral-600">{label}</label>
        <input type={type} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`whatsapp-${path}`} /></div>
    );
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#25D366] flex items-center justify-center"><MessageSquare className="w-5 h-5 text-white" /></div>
            <h1 className="font-display font-black text-3xl" data-testid="admin-whatsapp-title">WhatsApp Automation</h1>
          </div>
          <p className="text-neutral-500 mt-2">Configure the WhatsApp widget, auto-replies, and lead capture flow.</p>
        </div>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="whatsapp-save"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="grid gap-6">
        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Widget Configuration</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Business number (with country code, e.g. +919876543210)" path="whatsapp_auto.business_number" />
            <div>
              <label className="text-xs font-semibold text-neutral-600">Widget position</label>
              <select value={data.whatsapp_auto?.position || "bottom-left"} onChange={(e) => set("whatsapp_auto.position", e.target.value)} className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid="whatsapp-position">
                <option value="bottom-left">Bottom left</option>
                <option value="bottom-right">Bottom right</option>
              </select>
            </div>
            <F label="Greeting message (used in pre-filled WhatsApp)" path="whatsapp_auto.greeting" type="textarea" />
            <F label="Working hours" path="whatsapp_auto.working_hours" />
            <F label="Offline message" path="whatsapp_auto.offline_message" type="textarea" />
          </div>
          <div className="mt-4"><F label="Auto-reply enabled" path="whatsapp_auto.auto_reply_enabled" type="checkbox" /></div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Business API (optional)</h2>
          <p className="text-sm text-neutral-500 mb-4">Provide API credentials to enable server-side automated replies and template messages. When missing, the widget falls back to click-to-chat.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Phone Number ID" path="whatsapp_auto.phone_number_id" />
            <F label="Business Account ID" path="whatsapp_auto.business_account_id" />
            <F label="Access token" path="whatsapp_auto.access_token" type="password" />
            <F label="Webhook verify token" path="whatsapp_auto.webhook_verify_token" />
            <F label="Approved course-enquiry template name" path="whatsapp_auto.tpl_course_enquiry" />
            <F label="Approved syllabus template name" path="whatsapp_auto.tpl_syllabus" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h2 className="font-display font-bold text-lg mb-4">Lead Capture Before Redirect</h2>
          <F label="Show lead form before opening WhatsApp" path="whatsapp_auto.lead_form_enabled" type="checkbox" />
          <F label="Consent text" path="whatsapp_auto.consent_text" type="textarea" rows={2} />
          <F label="Human handover message" path="whatsapp_auto.handover_message" type="textarea" rows={2} />
        </div>
      </div>
    </div>
  );
}
